import * as Sentry from '@sentry/react';
import { ObjectLiteral, useErrorTracker } from '@viagogo/telemetry-react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ErrorDialog } from 'src/dialogs/ErrorDialog';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { REACT_APP_NAME } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import { ApiException, LoginClient } from 'src/WebApiController';
import {
  ClientLogClient,
  ClientLogRequest,
  LogLevel,
} from 'src/WebApiController';

import { useAppContext } from '../AppContext';
import { useContent } from '../ContentContext';

export type ErrorTypes =
  | ApiException
  | null
  | undefined
  | {
      status?: number | null;
      message?: string | null;
      response?: string | null;
    };

export type IClientLogContext = {
  trackError: (
    errorId: string,
    error: ErrorTypes,
    data?: ObjectLiteral
  ) => void;
  showErrorDialog: (
    errorId: string,
    error:
      | ApiException
      | null
      | undefined
      | {
          status?: number | null;
          message?: string | null;
          response?: string | null;
        },
    additionalOptions?: {
      onDismissError?: () => void;
      customerHeader?: React.ReactNode;
      trackErrorData?: ObjectLiteral;
    }
  ) => void;
  genericError: string;
  logEvent: (event: ClientLogRequest) => void; // Add method to manually log errors
};

export const ClientLogContext = React.createContext<IClientLogContext>({
  trackError: () => {},
  showErrorDialog: () => {},
  genericError: '',
  logEvent: () => {},
});

export const useClientLogContext = () => useContext(ClientLogContext);

/**
 * Adds a modal to the page where the provider is added
 * Provides utils to set all or update part of the modal
 * The modal is assumed to be open when it contains content
 */
export const ClientLogContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [trackError] = useErrorTracker();

  const { activeAccountWebClientConfig } = useAppContext();

  const genericError = useContent(
    ContentId.Error_InternalServerError_GenericMessage
  );

  const { dialogProps, closeDialog, launchDialog } = useBasicDialog();

  const [message, setMessage] = useState<string | null | undefined>();
  const [statusCode, setStatusCode] = useState<number | null | undefined>();
  const [customerHeader, setCustomerHeader] = useState<React.ReactNode>();
  const [onDismissError, setOnDismissError] = useState<() => void>();

  // Log buffering state
  const logBuffer = useRef<ClientLogRequest[]>([]);
  const flushTimer = useRef<NodeJS.Timeout | null>(null);
  const FLUSH_INTERVAL_MS = 5000; // 5 seconds

  const logClient = useMemo(
    () => new ClientLogClient(activeAccountWebClientConfig),
    [activeAccountWebClientConfig]
  );

  // Function to flush buffered logs
  const flushLogs = useCallback(() => {
    if (logBuffer.current.length === 0) return;

    const logsToSend = [...logBuffer.current];
    logBuffer.current = []; // Clear the buffer

    console.debug(`Flushing ${logsToSend.length} logs to server`);

    tryInvokeApi(
      async () => {
        await logClient.logEvents(logsToSend);
      },
      (error) => {
        console.warn('Failed to flush logs to server', error);
        // If flush fails, we could optionally re-add logs to buffer
        // logBuffer.current.unshift(...logsToSend);
      }
    );
  }, [logClient]);

  // Set up flush timer
  useEffect(() => {
    const startFlushTimer = () => {
      if (flushTimer.current) {
        clearInterval(flushTimer.current);
      }

      flushTimer.current = setInterval(() => {
        flushLogs();
      }, FLUSH_INTERVAL_MS);
    };

    startFlushTimer();

    // Cleanup on unmount
    return () => {
      if (flushTimer.current) {
        clearInterval(flushTimer.current);
        flushTimer.current = null;
      }
      // Flush any remaining logs before unmounting
      flushLogs();
    };
  }, [flushLogs]);

  // Flush logs when page is about to unload
  useEffect(() => {
    const handleBeforeUnload = () => {
      flushLogs();
    };

    const handlePageHide = () => {
      flushLogs();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handlePageHide);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handlePageHide);
    };
  }, [flushLogs]);

  const logEvent = useCallback(
    (logRequest: ClientLogRequest) => {
      // Add to buffer instead of immediate send
      logBuffer.current.push(logRequest);

      // Optional: Immediately flush if buffer gets too large
      if (logBuffer.current.length >= 50) {
        console.debug('Buffer full, flushing immediately');
        flushLogs();
      }
    },
    [flushLogs]
  );

  const trackErrorWrapper = useCallback(
    async (
      errorId: string,
      error:
        | ApiException
        | null
        | undefined
        | {
            status?: number | null;
            message?: string | null;
            response?: string | null;
          },
      data?: ObjectLiteral
    ) => {
      if (error?.status === 401) {
        // If we get an unauthorized, reload the page which will cause the login change to either
        // pull the updated login, or redirect to login page
        window.location.assign(`/login?returnUrl=${window.location.href}`);
      }

      const getLogLevel = (
        status: number | null | undefined,
        message: string
      ) => {
        // Uncomment this to break here to debug when we have errors
        // that we want to convert to a warning
        // debugger;
        if (status === 401) {
          return LogLevel.Warning;
        }

        if (
          message === 'Failed to fetch' ||
          message == 'NetworkError when attempting to fetch resource.'
        ) {
          return LogLevel.Warning;
        }

        return LogLevel.Error;
      };

      try {
        Sentry.captureException(error);
      } catch (e) {
        console.warn('Sentry log request failed', e);
      }

      const message = error?.response ?? error?.message ?? genericError;
      const statusStr = error?.status ? `[StatusCode]: ${error.status}` : '';

      data = { ...(data ?? {}), response: error?.response };

      const dataSerialized = JSON.stringify(data);

      console.error(`[${errorId}] - ${message}
[Details]:
${statusStr}
${dataSerialized}
`);

      trackError(errorId, message, {
        data: dataSerialized,
        status: error?.status,
        response: error?.response,
      });

      const logRequest: ClientLogRequest = {
        logLevel: getLogLevel(error?.status, message),
        applicationSource: REACT_APP_NAME,
        errorId: errorId,
        error: message,
        pageUrl: window.location.href,
        componentStack: null,
        timestamp: new Date().toISOString(),
        additionalInfo: {
          responseStatus: error?.status,
          data: dataSerialized,
        },
      };

      // Use buffered log instead of immediate send
      logEvent(logRequest);

      if (error?.status === 401) {
        // Check to see if the login-context is still good
        new LoginClient({}).getLoginContext('/').then((newLoginContext) => {
          if (
            !newLoginContext?.isSuccess ||
            newLoginContext?.user?.activeAccount?.id !==
              activeAccountWebClientConfig.activeAccountId
          ) {
            // Getting latest login unsuccessful, redirect to login to force re-sign-in
            window.location.assign(`/login?returnUrl=${window.location.href}`);
          }
        });
      }
    },
    [
      genericError,
      trackError,
      logEvent,
      activeAccountWebClientConfig.activeAccountId,
    ]
  );

  const showErrorDialog = useCallback(
    (
      errorId: string,
      error:
        | ApiException
        | null
        | undefined
        | {
            status?: number | null;
            message?: string | null;
            response?: string | null;
          },
      additionalOptions?: {
        onDismissError?: () => void;
        customerHeader?: React.ReactNode;
        trackErrorData?: ObjectLiteral;
      }
    ) => {
      const { onDismissError, customerHeader, trackErrorData } =
        additionalOptions || {};

      const message = error?.response ?? error?.message ?? genericError;

      setMessage(message);
      setStatusCode(error?.status);
      setCustomerHeader(customerHeader);
      setOnDismissError(onDismissError);

      trackErrorWrapper(errorId ?? 'Generic Error', error, trackErrorData);

      launchDialog();
    },
    [genericError, launchDialog, trackErrorWrapper]
  );

  const onErrorOk = useCallback(() => {
    closeDialog();

    onDismissError?.();

    // Clear all the error dialog props after dismiss
    setMessage('');
    setStatusCode(undefined);
    setCustomerHeader(undefined);
    setOnDismissError(undefined);
  }, [closeDialog, onDismissError]);

  return (
    <ClientLogContext.Provider
      value={{
        trackError: trackErrorWrapper,
        showErrorDialog,
        genericError,
        logEvent,
      }}
    >
      {children}
      <ErrorDialog
        {...dialogProps}
        headerText={customerHeader}
        messageText={message}
        statusCode={statusCode}
        onOkay={onErrorOk}
      />
    </ClientLogContext.Provider>
  );
};
